<script setup lang="ts">
import {
  computed,
  effectScope,
  onScopeDispose,
  onUnmounted,
  ref,
  watch,
  watchEffect,
  watchPostEffect,
} from 'vue';
import { useElementSize, watchImmediate } from '@vueuse/core';
import vtkPlane from '@kitware/vtk.js/Common/DataModel/Plane';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';
import vtkImageCPRMapper from '@kitware/vtk.js/Rendering/Core/ImageCPRMapper';
import { ProjectionMode } from '@kitware/vtk.js/Rendering/Core/ImageCPRMapper/Constants';
import '@kitware/vtk.js/Rendering/OpenGL/ImageCPRMapper';
import type { Bounds, Vector2, Vector3 } from '@kitware/vtk.js/types';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { useAutomaticPanoramicArch } from '@/src/composables/useAutomaticPanoramicArch';
import { usePanoramicRenderImage } from '@/src/composables/usePanoramicRenderImage';
import { useWindowingConfig } from '@/src/composables/useWindowingConfig';
import { useWindowingStore } from '@/src/store/view-configs/windowing';
import { onVTKEvent } from '@/src/composables/onVTKEvent';
import { useVtkView } from '@/src/core/vtk/useVtkView';
import { useResliceRepresentation } from '@/src/core/vtk/useResliceRepresentation';
import { useVtkRepresentation } from '@/src/core/vtk/useVtkRepresentation';
import type { View } from '@/src/core/vtk/types';
import { usePanoramicStore } from '@/src/store/view-configs/panoramic';
import { useResetViewsEvents } from '@/src/components/tools/ResetViews.vue';
import ViewTypeSwitcher from '@/src/components/ViewTypeSwitcher.vue';
import OriginalPanoramaButton from '@/src/components/dicom-web/OriginalPanoramaButton.vue';
import {
  archFrame,
  archFromLandmarks,
  clampToAxialBounds,
  isCompleteArch,
  locateOnArch,
  sampleArch,
} from '@/src/panoramic/geometry';

const props = defineProps<{ viewId: string }>();
const { currentImageID, currentImageData, isImageLoading } = useCurrentImage();
const panoramicStore = usePanoramicStore();
const windowingStore = useWindowingStore();
const axialContainer = ref<HTMLElement>();
const panoramicContainer = ref<HTMLElement>();
const transverseContainer = ref<HTMLElement>();
const supported = computed(() => {
  const image = currentImageData.value;
  return (
    !!image &&
    image.getDimensions().every((d) => d > 1) &&
    image.getPointData().getScalars()?.getNumberOfComponents() === 1
  );
});
const imageData = computed(() =>
  supported.value ? currentImageData.value : null
);
const bounds = computed(
  () => imageData.value?.getBounds() ?? ([0, 1, 0, 1, 0, 1] as Bounds)
);
const config = computed(() =>
  currentImageID.value && imageData.value
    ? panoramicStore.getConfig(currentImageID.value, bounds.value)
    : null
);
const automatic = useAutomaticPanoramicArch(imageData, config, isImageLoading);
const { detecting, notice: automaticNotice } = automatic;
const editing = ref(true);
const landmarks = ref<Vector2[]>([]);
const traceError = ref('');
const height = computed(() =>
  Math.max(
    1,
    Math.min(
      config.value?.verticalHeight ?? 80,
      bounds.value[5] - bounds.value[4]
    )
  )
);
const axialZ = computed(() =>
  Math.max(
    bounds.value[4],
    Math.min(
      bounds.value[5],
      config.value?.axialPosition ?? (bounds.value[4] + bounds.value[5]) / 2
    )
  )
);
const centerZ = computed(() =>
  Math.max(
    bounds.value[4] + height.value / 2,
    Math.min(bounds.value[5] - height.value / 2, axialZ.value)
  )
);
const samplingStep = computed(() =>
  Math.max(0.25, Math.min(...(imageData.value?.getSpacing() ?? [1, 1, 1])))
);
const samples = computed(() =>
  sampleArch(config.value?.points ?? [], centerZ.value, samplingStep.value)
);
const archLength = computed(() => samples.value.at(-1)?.distance ?? 0);
const selected = computed(() =>
  validTrace.value
    ? locateOnArch(samples.value, config.value?.position ?? 0.5)
    : null
);
const validTrace = computed(() =>
  isCompleteArch(config.value?.points ?? [], samples.value, bounds.value)
);
const placingLandmarks = computed(() => (config.value?.points.length ?? 0) < 5);
const traceInstruction = computed(() => {
  if (detecting.value) return 'Generando la panorámica automáticamente…';
  if (config.value?.curveSource === 'automatic' && validTrace.value)
    return 'Curva automática · Revise su ajuste; puede arrastrar los puntos para corregirla.';
  if (!placingLandmarks.value)
    return validTrace.value
      ? 'Arrastre los puntos de la curva para seguir el centro de los dientes.'
      : 'El trazado no recorre la arcada. Ajuste la curva en U o pulse Nuevo trazado.';
  return [
    '1 de 3 · Marque el centro del último molar de un lado.',
    '2 de 3 · Marque el centro de los incisivos, en la parte anterior.',
    '3 de 3 · Marque el último molar del lado opuesto.',
  ][landmarks.value.length];
});
const wl = useWindowingConfig(
  computed(() => props.viewId),
  currentImageID
);
function autoContrast() {
  if (currentImageID.value)
    windowingStore.updateConfig(props.viewId, currentImageID.value, {
      useAuto: true,
      auto: 'LowContrast',
    });
}
watchImmediate(currentImageID, (id) => {
  if (!id) return;
  const window = windowingStore.getConfig(props.viewId, id);
  if (!window.userTriggered && window.auto === 'FullRange') autoContrast();
});
function setWindowValue(event: Event, field: 'width' | 'level') {
  const input = event.target as HTMLInputElement;
  if (
    Number.isFinite(input.valueAsNumber) &&
    (field !== 'width' || input.valueAsNumber > 0)
  )
    wl[field].value = input.valueAsNumber;
  else input.value = String(wl[field].value);
}
const tracing = ref<{ index: number; pointer: number } | null>(null);
const screenVersion = ref(0);
let screenTick = 0;
function updateScreen() {
  screenVersion.value = ++screenTick;
}
const zoom = ref(1);
const axialZoom = ref(1);
const transverseWidth = computed(() =>
  Math.min(
    40,
    Math.max(
      10,
      bounds.value[1] - bounds.value[0],
      bounds.value[3] - bounds.value[2]
    )
  )
);

// Keep renderer disposal after all actors/watchers have left the scene graph.
function createPane(container: typeof axialContainer) {
  const scope = effectScope(true);
  const view = scope.run(() => useVtkView(container))!;
  view.renderer.setBackground(0, 0, 0);
  view.renderer.getActiveCamera().setParallelProjection(true);
  onUnmounted(() => scope.stop());
  onVTKEvent(view.renderer.getActiveCamera(), 'onModified', updateScreen);
  return view;
}
const axialView = createPane(axialContainer);
const panoramicView = createPane(panoramicContainer);
const transverseView = createPane(transverseContainer);
const {
  renderImage,
  preparing,
  error: renderError,
} = usePanoramicRenderImage(imageData, isImageLoading, () => {
  const gl = axialView.renderWindowView.get3DContext({});
  if (!gl || gl.isContextLost())
    throw new Error(
      'No hay una conexión gráfica disponible. Recargue el visor.'
    );
  return gl.getParameter(gl.MAX_3D_TEXTURE_SIZE);
});
watchPostEffect((onCleanup) => {
  const gl = axialView.renderWindowView.get3DContext({});
  if (!gl) return;
  const { canvas } = gl;
  const shaderFailed = () => {
    renderError.value =
      'El navegador no pudo dibujar la panorámica. Recargue el visor e intente de nuevo.';
  };
  const contextLost = (event: Event) => {
    event.preventDefault();
    renderError.value =
      'Se perdió la conexión con la tarjeta gráfica. Recargue el visor para volver a abrir la panorámica.';
  };
  canvas.addEventListener('vtk-shader-error', shaderFailed);
  canvas.addEventListener('webglcontextlost', contextLost);
  onCleanup(() => {
    canvas.removeEventListener('vtk-shader-error', shaderFailed);
    canvas.removeEventListener('webglcontextlost', contextLost);
  });
});
const renderInput = computed(() =>
  renderError.value ? null : renderImage.value
);
const axialRep = useResliceRepresentation(axialView, renderInput);
const transverseRep = useResliceRepresentation(
  transverseView,
  computed(() => (validTrace.value ? renderInput.value : null))
);
const cprRep = useVtkRepresentation({
  view: panoramicView,
  data: computed(() => (validTrace.value ? renderInput.value : null)),
  vtkActorClass: vtkImageSlice,
  vtkMapperClass: vtkImageCPRMapper,
});
const axialPlane = vtkPlane.newInstance({ normal: [0, 0, 1] });
const transversePlane = vtkPlane.newInstance();
const centerline = vtkPolyData.newInstance();
axialRep.mapper.setSlicePlane(axialPlane);
transverseRep.mapper.setSlicePlane(transversePlane);
axialRep.mapper.setSlabThickness(0);
transverseRep.mapper.setSlabThickness(0);
cprRep.mapper.setCenterlineData(centerline);
const orientationData = vtkDataArray.newInstance({
  name: 'Orientation',
  numberOfComponents: 4,
  values: new Float32Array(0),
});
centerline.getPointData().addArray(orientationData);
cprRep.mapper.useStraightenedMode();
onScopeDispose(() => {
  axialPlane.delete();
  transversePlane.delete();
  orientationData.delete();
  centerline.delete();
});

watchEffect(() => {
  for (const rep of [axialRep, transverseRep, cprRep]) {
    rep.property.setColorWindow(Math.max(1, wl.width.value));
    rep.property.setColorLevel(wl.level.value);
    rep.property.setInterpolationTypeToLinear();
  }
});
watchEffect(() => {
  axialPlane.setOrigin(
    (bounds.value[0] + bounds.value[1]) / 2,
    (bounds.value[2] + bounds.value[3]) / 2,
    axialZ.value
  );
  axialView.requestRender();
});
watchEffect(() => {
  const trace = samples.value;
  centerline
    .getPoints()
    .setData(new Float32Array(trace.flatMap((s) => s.point)), 3);
  centerline
    .getLines()
    .setData(
      trace.length > 1
        ? new Uint32Array([trace.length, ...trace.map((_, i) => i)])
        : new Uint32Array(0)
    );
  orientationData.setData(
    new Float32Array(trace.flatMap((s) => Array.from(archFrame(s.tangent)))),
    4
  );
  centerline.modified();
  cprRep.mapper.setWidth(height.value);
  cprRep.mapper.modified();
  panoramicView.requestRender();
});
watchEffect(() => {
  const state = config.value;
  if (!state) return;
  cprRep.mapper.setProjectionMode(
    state.projection === 'max' ? ProjectionMode.MAX : ProjectionMode.AVERAGE
  );
  cprRep.mapper.setProjectionSlabThickness(state.thickness);
  // Keep sample count odd and bounded for interactive rendering on mobile.
  cprRep.mapper.setProjectionSlabNumberOfSamples(
    Math.min(
      81,
      Math.max(3, 2 * Math.ceil(state.thickness / samplingStep.value / 2) + 1)
    )
  );
});
watchEffect(() => {
  const location = selected.value;
  if (!location) return;
  transversePlane.setOrigin(location.point);
  transversePlane.setNormal(location.tangent);
  transverseView.requestRender();
});

const axialSize = useElementSize(axialContainer);
const panoramicSize = useElementSize(panoramicContainer);
const transverseSize = useElementSize(transverseContainer);
function fitCamera(
  view: View,
  frame: {
    focal: Vector3;
    direction: Vector3;
    up: Vector3;
    width: number;
    vertical: number;
  },
  size: { width: { value: number }; height: { value: number } },
  scale = 1
) {
  const { focal, direction, up, width, vertical } = frame;
  const camera = view.renderer.getActiveCamera();
  const distance = Math.max(width, vertical) * 3 + 1;
  camera.setFocalPoint(...focal);
  camera.setPosition(
    focal[0] + direction[0] * distance,
    focal[1] + direction[1] * distance,
    focal[2] + direction[2] * distance
  );
  camera.setViewUp(...up);
  const aspect = Math.max(1, size.width.value) / Math.max(1, size.height.value);
  camera.setParallelScale(
    (Math.max(vertical / 2, width / (2 * aspect)) * 1.08) / scale
  );
  view.renderer.resetCameraClippingRange();
  view.requestRender();
}
watchEffect(() => {
  const b = bounds.value;
  fitCamera(
    axialView,
    {
      focal: [(b[0] + b[1]) / 2, (b[2] + b[3]) / 2, axialZ.value],
      direction: [0, 0, 1],
      up: [0, -1, 0],
      width: b[1] - b[0],
      vertical: b[3] - b[2],
    },
    axialSize,
    axialZoom.value
  );
});
watchEffect(() => {
  fitCamera(
    panoramicView,
    {
      focal: [height.value / 2, archLength.value / 2, 0],
      direction: [0, 0, 1],
      up: [1, 0, 0],
      width: Math.max(1, archLength.value),
      vertical: height.value,
    },
    panoramicSize,
    zoom.value
  );
});
watchEffect(() => {
  if (!selected.value) return;
  fitCamera(
    transverseView,
    {
      focal: selected.value.point,
      direction: selected.value.tangent,
      up: [0, 0, 1],
      width: transverseWidth.value,
      vertical: height.value,
    },
    transverseSize
  );
});
watch(
  [
    axialSize.width,
    axialSize.height,
    panoramicSize.width,
    panoramicSize.height,
  ],
  updateScreen
);
watch(currentImageID, () => {
  tracing.value = null;
  landmarks.value = [];
  traceError.value = '';
  editing.value = true;
  zoom.value = 1;
});
useResetViewsEvents().onClick(() => {
  zoom.value = 1;
});

function screenPoint(view: View, point: Vector3): Vector2 {
  const p = view.renderWindowView.worldToDisplay(...point, view.renderer);
  const [, h] = view.renderWindowView.getSize();
  return [p[0] / devicePixelRatio, (h - p[1]) / devicePixelRatio];
}
function worldPoint(view: View, event: PointerEvent): Vector3 {
  const rect = (event.currentTarget as Element).getBoundingClientRect();
  const focal = view.renderer.getActiveCamera().getFocalPoint();
  const [, , depth] = view.renderWindowView.worldToDisplay(
    ...focal,
    view.renderer
  );
  return view.renderWindowView.displayToWorld(
    (event.clientX - rect.left) * devicePixelRatio,
    (rect.height - event.clientY + rect.top) * devicePixelRatio,
    depth,
    view.renderer
  );
}
const axialControlPoints = computed(() => {
  void screenVersion.value;
  return (
    (placingLandmarks.value ? landmarks.value : config.value?.points)?.map(
      ([x, y]) => screenPoint(axialView, [x, y, axialZ.value])
    ) ?? []
  );
});
const axialPath = computed(() => {
  void screenVersion.value;
  if (placingLandmarks.value)
    return landmarks.value
      .map(([x, y]) => screenPoint(axialView, [x, y, axialZ.value]).join(','))
      .join(' ');
  return samples.value
    .map(({ point: [x, y] }) =>
      screenPoint(axialView, [x, y, axialZ.value]).join(',')
    )
    .join(' ');
});
const axialReference = computed(() => {
  void screenVersion.value;
  const s = selected.value;
  if (!s) return [];
  const [tx, ty] = s.tangent;
  const half = transverseWidth.value / 2;
  return [-1, 1].map((sign) =>
    screenPoint(axialView, [
      s.point[0] + sign * ty * half,
      s.point[1] - sign * tx * half,
      axialZ.value,
    ])
  );
});
const panoramicReference = computed(() => {
  void screenVersion.value;
  if (!selected.value) return [];
  return [0, height.value].map((x) =>
    screenPoint(panoramicView, [
      x,
      archLength.value - selected.value!.distance,
      0,
    ])
  );
});
function svgLine(points: Vector2[]) {
  return points.map((p) => p.join(',')).join(' ');
}

function placePoint(event: PointerEvent) {
  if (event.button !== 0 || !config.value) return;
  if (!editing.value || detecting.value) return;
  const [x, y] = worldPoint(axialView, event);
  const p = clampToAxialBounds([x, y], bounds.value);
  if (placingLandmarks.value) {
    landmarks.value.push(p);
    traceError.value = '';
    if (landmarks.value.length === 3) {
      const arch = archFromLandmarks(landmarks.value);
      if (arch) {
        config.value.points = arch.map((point) =>
          clampToAxialBounds(point, bounds.value)
        );
        landmarks.value = [];
      } else {
        traceError.value =
          'Las tres referencias deben formar una U: un molar, los incisivos y el molar opuesto. Vuelva a marcarlas.';
        landmarks.value = [];
      }
    }
    return;
  }
  const screen = screenPoint(axialView, [p[0], p[1], axialZ.value]);
  const distances = axialControlPoints.value.map((q) =>
    Math.hypot(q[0] - screen[0], q[1] - screen[1])
  );
  const nearest = Math.min(...distances);
  const existing = nearest < 14 ? distances.indexOf(nearest) : -1;
  if (existing < 0) return;
  const index = existing;
  tracing.value = { index, pointer: event.pointerId };
  (event.currentTarget as Element).setPointerCapture(event.pointerId);
}
function movePoint(event: PointerEvent) {
  if (
    !tracing.value ||
    tracing.value.pointer !== event.pointerId ||
    !config.value
  )
    return;
  const [x, y] = worldPoint(axialView, event);
  config.value.points[tracing.value.index] = clampToAxialBounds(
    [x, y],
    bounds.value
  );
  config.value.curveSource = 'manual';
}
function finishPoint() {
  tracing.value = null;
}
function removePoint(index: number) {
  automatic.cancel();
  if (config.value) config.value.curveSource = 'manual';
  if (placingLandmarks.value) {
    landmarks.value.splice(index, 1);
    return;
  }
  if ((config.value?.points.length ?? 0) <= 5) return;
  config.value?.points.splice(index, 1);
}
function selectPosition(event: PointerEvent) {
  if (
    (event.type === 'pointerdown' && event.button !== 0) ||
    !config.value ||
    !validTrace.value
  )
    return;
  const [, y] = worldPoint(panoramicView, event);
  config.value.position = Math.max(0, Math.min(1, 1 - y / archLength.value));
}
function wheelAxial(event: WheelEvent) {
  if (!config.value || !event.deltaY) return;
  config.value.axialPosition = Math.max(
    bounds.value[4],
    Math.min(
      bounds.value[5],
      axialZ.value + Math.sign(event.deltaY) * samplingStep.value
    )
  );
}
function resetTrace() {
  automatic.cancel();
  if (config.value) {
    config.value.points = [];
    config.value.curveSource = 'manual';
  }
  tracing.value = null;
  editing.value = true;
  landmarks.value = [];
  traceError.value = '';
}
watchImmediate(imageData, () => {
  zoom.value = 1;
});
</script>

<template>
  <section
    class="panoramic-workspace"
    data-testid="panoramic-workspace"
    @dblclick.stop
  >
    <header class="panoramic-header">
      <div>
        <strong>Panorámica dental</strong><span>{{ traceInstruction }}</span>
      </div>
      <OriginalPanoramaButton />
      <ViewTypeSwitcher :view-id="viewId" :image-id="currentImageID" />
    </header>
    <div
      v-if="traceError"
      class="panoramic-notice panoramic-error"
      role="alert"
    >
      {{ traceError }}
    </div>
    <div
      v-if="detecting || automaticNotice"
      class="panoramic-notice"
      role="status"
    >
      {{
        detecting ? 'Buscando la arcada dental en el volumen…' : automaticNotice
      }}
    </div>
    <div v-if="!supported" class="panoramic-notice" role="status">
      {{
        isImageLoading
          ? 'Cargando volumen…'
          : 'Cargue un volumen 3D de tomografía para reconstruir la panorámica.'
      }}
    </div>
    <div v-if="preparing" class="panoramic-notice" role="status">
      Preparando la imagen panorámica…
    </div>
    <div
      v-if="renderError"
      class="panoramic-notice panoramic-error"
      role="alert"
    >
      {{ renderError }}
    </div>
    <div class="panoramic-grid">
      <div class="panoramic-guides">
        <section class="panoramic-panel">
          <h3>
            Axial · Arcada
            <span class="axial-zoom-controls"
              ><button
                type="button"
                aria-label="Alejar guía axial"
                @click="axialZoom = Math.max(0.75, axialZoom - 0.25)"
              >
                −</button
              ><button
                type="button"
                aria-label="Acercar guía axial"
                @click="axialZoom = Math.min(3, axialZoom + 0.25)"
              >
                +
              </button></span
            >
          </h3>
          <div
            class="panoramic-canvas"
            data-testid="panoramic-axial"
            @wheel.prevent="wheelAxial"
          >
            <div
              ref="axialContainer"
              class="panoramic-render"
              data-testid="vtk-view"
            />
            <svg
              class="panoramic-overlay"
              aria-label="Trazar arcada dental"
              @pointerdown.prevent="placePoint"
              @pointermove="movePoint"
              @pointerup="finishPoint"
              @pointercancel="finishPoint"
              @lostpointercapture="finishPoint"
              @contextmenu.prevent
            >
              <polyline :points="axialPath" class="arch-line" />
              <polyline
                :points="svgLine(axialReference)"
                class="reference-line"
              />
              <circle
                v-for="(point, index) in axialControlPoints"
                :key="index"
                :cx="point[0]"
                :cy="point[1]"
                r="5"
                class="arch-point"
                data-testid="arch-point"
                @contextmenu.prevent.stop="removePoint(index)"
              />
            </svg>
          </div>
          <label class="panoramic-range"
            >Corte axial
            <input
              v-if="config"
              v-model.number="config.axialPosition"
              aria-label="Corte axial de trazado"
              type="range"
              :min="bounds[4]"
              :max="bounds[5]"
              step="any"
          /></label>
          <p class="panoramic-caption" data-testid="arch-instruction">
            {{ traceInstruction }}
          </p>
        </section>
        <section class="panoramic-panel">
          <h3>Corte transversal</h3>
          <div class="panoramic-canvas" data-testid="panoramic-transverse">
            <div
              ref="transverseContainer"
              class="panoramic-render"
              data-testid="vtk-view"
            />
            <p v-if="!validTrace" class="panoramic-hint">
              El corte se vincula al recorrido de la arcada.
            </p>
          </div>
          <label class="panoramic-range"
            >Recorrer arcada
            <input
              v-if="config"
              v-model.number="config.position"
              aria-label="Posición del corte transversal"
              type="range"
              min="0"
              max="1"
              step="0.001"
              :disabled="!validTrace"
          /></label>
        </section>
      </div>
      <section class="panoramic-panel panoramic-main">
        <h3>
          Panorámica CPR
          <span v-if="validTrace"
            >{{ archLength.toFixed(1) }} mm de recorrido</span
          >
        </h3>
        <div class="panoramic-canvas" data-testid="panoramic-cpr">
          <div
            ref="panoramicContainer"
            class="panoramic-render"
            data-testid="vtk-view"
          />
          <svg
            class="panoramic-overlay"
            aria-label="Elegir corte en panorámica"
            @pointerdown.prevent="selectPosition"
            @pointermove="$event.buttons === 1 && selectPosition($event)"
          >
            <polyline
              :points="svgLine(panoramicReference)"
              class="reference-line"
            />
          </svg>
          <p v-if="!validTrace" class="panoramic-hint">
            <template v-if="detecting">Generando panorámica…</template>
            <template v-else>
              <strong>Puede definir la arcada manualmente</strong><br />
              Marque tres referencias sobre los dientes en el cuadro axial:<br />
              molar de un lado → incisivos → molar del otro lado.<br />
              El visor generará una curva en U que podrá ajustar.
            </template>
          </p>
        </div>
        <p class="panoramic-caption">
          <template v-if="renderImage && renderImage !== imageData"
            >Resolución adaptada para visualización.
          </template>
          Haga clic en la panorámica para mover el corte transversal.
        </p>
      </section>
    </div>
    <footer v-if="config" class="panoramic-controls" @pointerdown.stop>
      <button type="button" @click="autoContrast">Contraste auto</button>
      <button type="button" :disabled="detecting" @click="automatic.generate">
        {{ detecting ? 'Generando…' : 'Generar automáticamente' }}
      </button>
      <button type="button" :aria-pressed="editing" @click="editing = !editing">
        {{ editing ? '✓ Editar arcada' : 'Editar arcada' }}
      </button>
      <button
        v-if="placingLandmarks"
        type="button"
        :disabled="!landmarks.length"
        @click="landmarks.pop()"
      >
        Deshacer referencia
      </button>
      <button type="button" @click="resetTrace">Trazar manualmente</button>
      <label
        >Altura
        <select
          v-model.number="config.verticalHeight"
          aria-label="Altura de panorámica"
        >
          <option
            v-for="value in [40, 60, 80, 100, 120, 160]"
            :key="value"
            :value="value"
          >
            {{ value }} mm
          </option>
        </select></label
      >
      <label
        >Espesor
        <select
          v-model.number="config.thickness"
          aria-label="Espesor de panorámica"
        >
          <option
            v-for="value in [1, 3, 5, 10, 15, 20, 30]"
            :key="value"
            :value="value"
          >
            {{ value }} mm
          </option>
        </select></label
      >
      <label
        >Proyección
        <select
          v-model="config.projection"
          aria-label="Proyección de panorámica"
        >
          <option value="average">Promedio</option>
          <option value="max">Máxima intensidad</option>
        </select></label
      >
      <label
        >Brillo
        <input
          :value="wl.level.value"
          @change="setWindowValue($event, 'level')"
          aria-label="Brillo de panorámica"
          type="number"
          step="50"
      /></label>
      <label
        >Contraste
        <input
          :value="wl.width.value"
          @change="setWindowValue($event, 'width')"
          aria-label="Contraste de panorámica"
          type="number"
          min="1"
          step="50"
      /></label>
      <label
        >Zoom
        <input
          v-model.number="zoom"
          aria-label="Zoom de panorámica"
          type="range"
          min="0.5"
          max="3"
          step="0.05"
      /></label>
    </footer>
  </section>
</template>

<style scoped src="@/src/components/styles/panoramic-view.css"></style>
